/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Stack, Typography, paletteColors } from '@evaneos/b2b-ui';

import {
    getSidebarContactText,
    getSidebarJoursText,
} from '../../../resources/appointment/operations';
import type { ContactType, DayKey } from '../../../resources/appointment/operations';

type Section = 'slots' | 'contact' | 'params';

const SECTIONS: { id: Section; label: string }[] = [
    { id: 'slots', label: 'Créneaux hebdomadaires' },
    { id: 'contact', label: 'Moyen de contact privilégié' },
    { id: 'params', label: 'Paramètres de rendez-vous' },
];

type Props = {
    activeSection: Section;
    activeDays: Partial<Record<DayKey, boolean>>;
    defaultCallType: ContactType;
    slotDurationInMinutes: number;
    travelerReminder: boolean;
    onSectionClick: (section: Section) => void;
};

export const AppointmentSidebarPartial = ({
    activeSection,
    activeDays,
    defaultCallType,
    slotDurationInMinutes,
    travelerReminder,
    onSectionClick,
}: Props): JSX.Element => (
    <Box
        sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            position: 'sticky',
            top: 80,
            alignSelf: 'start',
        }}
    >
        <Stack spacing={0.5}>
            {SECTIONS.map(({ id, label }) => {
                const isActive = activeSection === id;
                return (
                    <Box
                        key={id}
                        component="button"
                        data-spec-id={
                            id === 'contact' ? 'sidebar-contact-row' : `sidebar-anchor-${id}`
                        }
                        onClick={() => onSectionClick(id)}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.25,
                            p: 2,
                            borderRadius: 1,
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            bgcolor: isActive ? paletteColors.green[100] : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': {
                                bgcolor: isActive
                                    ? paletteColors.green[100]
                                    : paletteColors.neutral[100],
                            },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color: isActive ? paletteColors.green[800] : 'text.secondary',
                                lineHeight: 1.4,
                            }}
                        >
                            {label}
                        </Typography>

                        {id === 'slots' && (
                            <Typography
                                data-spec-id="sidebar-jours-chip"
                                sx={{
                                    fontSize: 12,
                                    color: paletteColors.neutral[500],
                                    lineHeight: 1.3,
                                }}
                            >
                                {getSidebarJoursText(activeDays)}
                            </Typography>
                        )}

                        {id === 'contact' && (
                            <Typography
                                data-spec-id="sidebar-contact-chip"
                                sx={{
                                    fontSize: 12,
                                    color: paletteColors.neutral[500],
                                    lineHeight: 1.3,
                                }}
                            >
                                {getSidebarContactText(defaultCallType)}
                            </Typography>
                        )}

                        {id === 'params' && (
                            <>
                                <Typography
                                    data-spec-id="sidebar-duree-chip"
                                    sx={{
                                        fontSize: 12,
                                        color: paletteColors.neutral[500],
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {slotDurationInMinutes} min
                                </Typography>
                                <Typography
                                    data-spec-id="sidebar-rappel-chip"
                                    sx={{
                                        fontSize: 12,
                                        color: paletteColors.neutral[500],
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {/* @todo translate: agenda.sidebar.rappel_chip */}
                                    {travelerReminder ? 'Activé' : 'Désactivé'}
                                </Typography>
                            </>
                        )}
                    </Box>
                );
            })}
        </Stack>
    </Box>
);
